import { useState } from 'react';
import { BookSuggestion } from '@/types/chat';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { ThumbsUp, BookOpen, CheckCircle, X } from 'lucide-react';
import { CoverImage } from './CoverImage';

interface BookClubSuggestionsProps {
  suggestions: BookSuggestion[];
  onUpdateStatus: (suggestionId: string, status: string) => void;
  onVote: (suggestionId: string) => void;
  loading?: boolean;
}

export const BookClubSuggestions = ({
  suggestions,
  onUpdateStatus,
  onVote,
  loading = false
}: BookClubSuggestionsProps) => {
  const [activeTab, setActiveTab] = useState('suggested');

  const filteredSuggestions = suggestions.filter(s => s.status === activeTab);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { label: string; variant: any }> = {
      suggested: { label: 'Suggested', variant: 'secondary' },
      reading: { label: 'Currently Reading', variant: 'default' },
      completed: { label: 'Completed', variant: 'outline' },
      skipped: { label: 'Skipped', variant: 'destructive' }
    };
    return variants[status] || variants.suggested;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          📚 Book Club Reading List
        </h3>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="mx-4 mt-4">
          <TabsTrigger value="suggested">Suggested</TabsTrigger>
          <TabsTrigger value="reading">Reading</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="text-center text-muted-foreground py-8">Loading...</div>
          ) : filteredSuggestions.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No books {activeTab === 'suggested' ? 'suggested' : activeTab} yet
            </div>
          ) : (
            filteredSuggestions.map((suggestion) => (
              <Card key={suggestion.id} className="p-4">
                <div className="flex gap-4">
                  <div className="w-20 h-28 flex-shrink-0">
                    <CoverImage
                      bookId={suggestion.book_id}
                      coverUrl={suggestion.book?.cover_url}
                      title={suggestion.book?.title}
                      author={suggestion.book?.authors?.[0]}
                      size="sm"
                      className="w-full h-full"
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold truncate">{suggestion.book?.title}</h4>
                    <p className="text-sm text-muted-foreground truncate">
                      {suggestion.book?.authors?.join(', ')}
                    </p>
                    
                    <div className="flex items-center gap-2 mt-2">
                      <Badge {...getStatusBadge(suggestion.status)} />
                      <span className="text-xs text-muted-foreground">
                        by @{suggestion.suggester?.username}
                      </span>
                    </div>

                    {suggestion.notes && (
                      <p className="text-sm mt-2 text-muted-foreground">{suggestion.notes}</p>
                    )}

                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onVote(suggestion.id)}
                        className="gap-1"
                      >
                        <ThumbsUp className="w-3 h-3" />
                        {suggestion.vote_count || 0}
                      </Button>

                      {activeTab === 'suggested' && (
                        <Button
                          size="sm"
                          onClick={() => onUpdateStatus(suggestion.id, 'reading')}
                          className="gap-1"
                        >
                          <BookOpen className="w-3 h-3" />
                          Start Reading
                        </Button>
                      )}

                      {activeTab === 'reading' && (
                        <Button
                          size="sm"
                          onClick={() => onUpdateStatus(suggestion.id, 'completed')}
                          className="gap-1"
                        >
                          <CheckCircle className="w-3 h-3" />
                          Mark Complete
                        </Button>
                      )}

                      {activeTab === 'suggested' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onUpdateStatus(suggestion.id, 'skipped')}
                          className="gap-1"
                        >
                          <X className="w-3 h-3" />
                          Skip
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </Tabs>
    </div>
  );
};
